/**
 * **Wer rendert wie oft, und woran lag es.**
 *
 * Der Reporter in `TabStack` sagt, DASS ein Commit 200ms gebraucht hat
 * (`[TabStack/slow]`, gemessen am 21.09.2026 nach dem Anlegen einer Aufgabe).
 * Er sagt nicht, welcher Baustein so oft neu gerendert hat und welche Prop ihn
 * dazu gebracht hat. Genau das steht hier.
 *
 * Die Zahlen werden je Bild gesammelt und einmal ausgegeben — sonst schriebe
 * eine Liste mit vierzig Zeilen vierzig Zeilen Protokoll und verfälschte die
 * Messung gleich mit.
 *
 * **Nur im Dev-Betrieb**, und auch dort nur, wenn es eingeschaltet ist:
 * `localStorage.setItem('render-audit', '1')`, dann neu laden. Im
 * Produktionsbau ist die Funktion ein leerer Aufruf.
 *
 * @example
 * // In der Komponente, hinter den Hooks:
 * if (renderAuditOn) auditRender('TaskEntry', key.current, { task, selected, onToggleComplete })
 */

const isDev = process.env.NODE_ENV !== 'production'

function readFlag(): boolean {
  if (!isDev) return false
  try {
    return localStorage.getItem('render-audit') === '1'
  } catch {
    return false
  }
}

/**
 * **Einmal beim Laden gelesen, nicht bei jedem Render.**
 *
 * Aufrufer stellen ihren Prop-Satz als Objekt-Literal zusammen — bei einer
 * Aufgabenzeile sind das zwei Dutzend Felder, mal fünfunddreißig Zeilen. Wäre
 * die Prüfung erst IM Aufruf, entstünde dieses Objekt auch im Produktionsbau
 * und auch bei ausgeschaltetem Protokoll. Darum fragen Aufrufer vorher:
 * `if (renderAuditOn) auditRender(...)`. Umschalten braucht ohnehin ein
 * Neuladen (`localStorage.setItem('render-audit', '1')`).
 */
export const renderAuditOn = readFlag()

interface Bucket {
  renders: number
  /** Prop-Name → wie oft sie sich unterschied. */
  changed: Map<string, number>
}

const buckets = new Map<string, Bucket>()
let flushScheduled = false

function scheduleFlush(): void {
  if (flushScheduled) return
  flushScheduled = true
  requestAnimationFrame(() => {
    flushScheduled = false
    if (buckets.size === 0) return
    const lines: string[] = []
    for (const [name, bucket] of buckets) {
      const reasons = [...bucket.changed.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([prop, count]) => `${prop}×${count}`)
        .join(', ')
      lines.push(`  ${name}: ${bucket.renders} Renders${reasons ? ' — ' + reasons : ' — ohne Prop-Wechsel'}`)
    }
    buckets.clear()
    console.warn('[render-audit]\n' + lines.join('\n'))
  })
}

/** Der vorige Prop-Satz je Aufrufstelle. Ein WeakMap-Schlüssel geht nicht: Die Props sind neue Objekte. */
const previous = new WeakMap<object, Record<string, unknown>>()

/**
 * Zählt den Render und meldet, welche der übergebenen Props sich seit dem
 * letzten Mal unterschieden haben — verglichen wird die Identität, denn genau
 * daran entscheidet `React.memo`.
 *
 * `key` ist ein stabiles Objekt je Instanz (ein Ref-Objekt reicht); ohne eines
 * wird nur gezählt, nicht verglichen.
 */
export function auditRender(name: string, key: object, props: Record<string, unknown>): void {
  if (!renderAuditOn) return

  let bucket = buckets.get(name)
  if (!bucket) {
    bucket = { renders: 0, changed: new Map() }
    buckets.set(name, bucket)
  }
  bucket.renders += 1

  const before = previous.get(key)
  if (before) {
    for (const prop of Object.keys(props)) {
      if (before[prop] !== props[prop]) {
        bucket.changed.set(prop, (bucket.changed.get(prop) ?? 0) + 1)
      }
    }
  }
  previous.set(key, { ...props })

  scheduleFlush()
}
